import { useAuthorizeMonthlyExpenseMutation } from '@/app/services/monthlyExpenseApi';
import { usePeriodsQuery } from '@/app/services/periodApi';
import { useAuthorizeSharingMutation } from '@/app/services/sharingApi';
import { datetimeNewLabelFormated } from '@/app/utils/dateUtils';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, Card, CardContent, Chip, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { green, yellow } from '@mui/material/colors';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import StandardDialog from '../common/StandardDialog';
import PeriodSelect from './PeriodSelect';

function PeriodDetails() {
  const theme = useTheme();

  const { i18n, t } = useTranslation(['periodDetails', 'common']);

  const { selectedPeriod, setSelectedPeriod } = usePeriod();

  const [openProcessDialog, setOpenProcessDialog] = useState(false);
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const searchSize = 5;

  const { data: periods, isSuccess, refetch } = usePeriodsQuery(searchSize);

  const [sharingAuthorization] = useAuthorizeSharingMutation();
  const [authorizeMonthlyExpense] = useAuthorizeMonthlyExpenseMutation();

  useEffect(() => {
    if (isSuccess && periods?.content && !selectedPeriod) {
      setSelectedPeriod(periods.content[0]);
    }
  }, [selectedPeriod, setSelectedPeriod, periods, isSuccess]);

  const synchronizeSharing = async () => {
    await toast.promise(
      sharingAuthorization(selectedPeriod?.date || ''),
      {
        pending: t('authorize.sharing.sending') as string,
        success: t('authorize.sharing.success') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    refetch();
  };

  const synchronizeEvents = async () => {
    await toast.promise(
      authorizeMonthlyExpense(selectedPeriod?.date || ''),
      {
        pending: t('authorize.event.sending') as string,
        success: t('authorize.event.success') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    refetch();
  };

  return (
    <Card sx={{ boxShadow: 3, display: 'flex', height: theme.spacing(9), alignItems: 'center' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', pt: 3, width: '100%' }}>
        <PeriodSelect selectedPeriod={selectedPeriod} periods={periods} setSelectedPeriod={setSelectedPeriod} />
        {selectedPeriod && (
          <Box
            sx={{
              display: 'flex',
              textAlign: 'center',
              justifyContent: 'space-between',
              width: '70%',
              mx: 2,
            }}
          >
            <Box sx={{ display: 'flex', textAlign: 'center', justifyContent: 'center' }}>
              <Box sx={{ display: 'block', ml: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      mr: 2,
                    }}
                    variant="h3"
                  >
                    {`${t('resource.sharing')}:`}
                  </Typography>
                  <Tooltip
                    placement="top"
                    title={
                      !selectedPeriod.directSharingAuthorized
                        ? t(
                            'the.direct.sharing.system.has.sent.the.headcount.information.and.needs.authorization.to.start.the.import.process',
                          )
                        : t(
                            'authorized.means.the.selected.period.has.already.been.set.up.according.to.the.headcount.information.sent.by.direct.allocation',
                          )
                    }
                  >
                    {!selectedPeriod.directSharingAuthorized ? (
                      <Chip
                        sx={{
                          color: yellow[800],
                          maxHeight: theme.spacing(3),
                          width: theme.spacing(16),
                          borderRadius: theme.spacing(1),
                          ml: 1,
                        }}
                        label={t('pending')}
                      />
                    ) : (
                      <Chip
                        sx={{
                          background: green.A100,
                          color: green[800],
                          maxHeight: theme.spacing(3),
                          width: theme.spacing(16),
                          borderRadius: theme.spacing(1),
                          ml: 1,
                        }}
                        label={t('authorized')}
                      />
                    )}
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="h3">{`${t('last.update')}:`}</Typography>
                  <Typography sx={{ ml: 1 }} variant="subtitleSmaller">
                    {!selectedPeriod.directSharingSynchronized
                      ? '__/__/____ às __:__h'
                      : datetimeNewLabelFormated(selectedPeriod.directSharingSynchronizedDate as string, t('at'))}
                  </Typography>
                </Box>
              </Box>
              <StandardDialog
                title={t('confirm.authorization.title')}
                contentText={`${t('confirm.authorization.message.part1')} ${
                  selectedPeriod.date
                    ? `${DateTime.fromISO(selectedPeriod.date).setLocale(i18n.language).toFormat('LLLL/yyyy')}`
                    : '--/--'
                }? ${t('confirm.authorization.message.part2')}`}
                open={openProcessDialog}
                onClose={() => setOpenProcessDialog(false)}
                onConfirm={() => {
                  setOpenProcessDialog(false);
                  synchronizeSharing();
                }}
                cancelText={t('common:no')}
                confirmText={t('common:yes')}
              />

              <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 2 }}>
                <Button
                  variant="primary"
                  disabled={!selectedPeriod || selectedPeriod.directSharingAuthorized}
                  onClick={() => {
                    setOpenProcessDialog(true);
                  }}
                  sx={{ width: theme.spacing(16) }}
                >
                  {t('authorize')}
                </Button>
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', textAlign: 'center', justifyContent: 'center' }}>
              <Box sx={{ display: 'block', ml: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      mr: 2,
                    }}
                    variant="h3"
                  >
                    {`${t('events')}:`}
                  </Typography>
                  <Tooltip
                    placement="top"
                    title={
                      !selectedPeriod.expensesAuthorized
                        ? t(
                            'the.direct.sharing.system.has.sent.the.headcount.information.and.needs.authorization.to.start.the.import.process',
                          )
                        : t(
                            'authorized.means.the.selected.period.has.already.been.set.up.according.to.the.headcount.information.sent.by.direct.allocation',
                          )
                    }
                    sx={{ ml: 1 }}
                  >
                    {!selectedPeriod.directSharingAuthorized ? (
                      <Chip
                        sx={{
                          color: yellow[800],
                          maxHeight: theme.spacing(3),
                          width: theme.spacing(16),
                          borderRadius: theme.spacing(1),
                          ml: 1,
                        }}
                        label={t('pending')}
                      />
                    ) : (
                      <Chip
                        sx={{
                          background: green.A100,
                          color: green[800],
                          maxHeight: theme.spacing(3),
                          width: theme.spacing(16),
                          borderRadius: theme.spacing(1),
                          ml: 5,
                        }}
                        label={t('authorized')}
                      />
                    )}
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="h3">{`${t('last.update')}:`}</Typography>
                  <Typography sx={{ ml: 1 }} variant="subtitleSmaller">
                    {!selectedPeriod.expensesSynchronized
                      ? t('format.hours')
                      : datetimeNewLabelFormated(selectedPeriod.expensesSynchronizedDate as string, t('at'))}
                  </Typography>
                </Box>
              </Box>
              <StandardDialog
                title={t('confirm.synchronization.title')}
                contentText={`${t('confirm.synchronization.message.part1')} ${
                  selectedPeriod.date
                    ? `${DateTime.fromISO(selectedPeriod.date).setLocale(i18n.language).toFormat('LLLL/yyyy')}`
                    : '--/--'
                }? ${t('confirm.synchronization.message.part2')}`}
                open={openSaveDialog}
                onClose={() => setOpenSaveDialog(false)}
                onConfirm={() => {
                  setOpenSaveDialog(false);
                  synchronizeEvents();
                }}
                cancelText={t('common:no')}
                confirmText={t('common:yes')}
              />

              <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 2 }}>
                <Button
                  disabled={!selectedPeriod || selectedPeriod.expensesAuthorized}
                  variant="primary"
                  onClick={() => {
                    setOpenSaveDialog(true);
                  }}
                  sx={{ width: theme.spacing(16) }}
                >
                  {t('authorize')}
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default PeriodDetails;
